import Dexie, { type Table } from 'dexie'
import type { Task, Category, CompletionLog, Attachment, TaskTemplate, ActivityLog, TaskGroup } from '@/lib/types'
import { formatDate } from '@/utils/format'

export interface AIChatMessage {
  id?: number
  role: 'user' | 'assistant'
  content: string
  createdAt: string
}

class TodoDatabase extends Dexie {
  tasks!: Table<Task>
  categories!: Table<Category>
  completionLogs!: Table<CompletionLog>
  attachments!: Table<Attachment>
  templates!: Table<TaskTemplate>
  activityLogs!: Table<ActivityLog>
  taskGroups!: Table<TaskGroup>
  aiChatMessages!: Table<AIChatMessage>

  constructor() {
    super('TodoList')
    this.version(1).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder',
      categories: '++id, name, isDefault, sortOrder',
      completionLogs: '++id, taskId, date, completedAt',
    })

    this.version(2).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder',
      categories: '++id, name, isDefault, sortOrder',
      completionLogs: '++id, taskId, date, completedAt',
      attachments: '++id, taskId, createdAt',
      templates: '++id, name, categoryId, createdAt',
      activityLogs: '++id, taskId, action, date, createdAt',
    })

    this.version(3).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder, syncId',
      categories: '++id, name, isDefault, sortOrder, syncId',
      completionLogs: '++id, taskId, date, completedAt, syncId',
      attachments: '++id, taskId, createdAt',
      templates: '++id, name, categoryId, createdAt',
      activityLogs: '++id, taskId, action, date, createdAt',
    }).upgrade(async (tx) => {
      const tasks = await tx.table('tasks').toArray()
      for (const task of tasks) {
        await tx.table('tasks').update(task.id, { syncId: crypto.randomUUID() })
      }
      const categories = await tx.table('categories').toArray()
      for (const cat of categories) {
        await tx.table('categories').update(cat.id, {
          syncId: crypto.randomUUID(),
          updatedAt: cat.createdAt,
        })
      }
      const logs = await tx.table('completionLogs').toArray()
      for (const log of logs) {
        await tx.table('completionLogs').update(log.id, { syncId: crypto.randomUUID() })
      }
    })

    this.version(4).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder, syncId',
      categories: '++id, name, isDefault, sortOrder, syncId, parentId',
      completionLogs: '++id, taskId, date, completedAt, syncId',
      attachments: '++id, taskId, createdAt',
      templates: '++id, name, categoryId, createdAt',
      activityLogs: '++id, taskId, action, date, createdAt',
    })

    this.version(5).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder, syncId',
      categories: '++id, name, isDefault, sortOrder, syncId, parentId',
      completionLogs: '++id, taskId, date, completedAt, syncId',
      attachments: '++id, taskId, createdAt',
      templates: '++id, name, categoryId, createdAt',
      activityLogs: '++id, taskId, action, date, createdAt',
      taskGroups: '++id, name, sortOrder, syncId, createdAt',
    })

    this.version(6).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder, syncId, googleTaskId',
      categories: '++id, name, isDefault, sortOrder, syncId, parentId',
      completionLogs: '++id, taskId, date, completedAt, syncId',
      attachments: '++id, taskId, createdAt',
      templates: '++id, name, categoryId, createdAt',
      activityLogs: '++id, taskId, action, date, createdAt',
      taskGroups: '++id, name, sortOrder, syncId, createdAt',
    })

    this.version(7).stores({
      tasks: '++id, categoryId, status, dueDate, isFlagged, isStarred, createdAt, sortOrder, syncId, googleTaskId',
      categories: '++id, name, isDefault, sortOrder, syncId, parentId',
      completionLogs: '++id, taskId, date, completedAt, syncId',
      attachments: '++id, taskId, createdAt',
      templates: '++id, name, categoryId, createdAt',
      activityLogs: '++id, taskId, action, date, createdAt',
      taskGroups: '++id, name, sortOrder, syncId, createdAt',
      aiChatMessages: '++id, role, createdAt',
    })
  }
}

const db = new TodoDatabase()

db.on('populate', () => {
  db.categories.bulkAdd([
    { name: '작업', color: '#3B82F6', isDefault: true, sortOrder: 0, createdAt: new Date().toISOString() },
    { name: '개인', color: '#10B981', isDefault: true, sortOrder: 1, createdAt: new Date().toISOString() },
    { name: '위시리스트', color: '#F59E0B', isDefault: true, sortOrder: 2, createdAt: new Date().toISOString() },
  ])
})

export { db }

// ─── Task CRUD ─────────────────────────────────────
export async function getAllTasks(filter?: { status?: string }): Promise<Task[]> {
  if (filter?.status) {
    return db.tasks.where('status').equals(filter.status).toArray()
  }
  return db.tasks.toArray()
}

export async function getTask(id: number): Promise<Task | undefined> {
  return db.tasks.get(id)
}

export async function addTask(task: Omit<Task, 'id'>): Promise<number> {
  return db.tasks.add(task as Task) as Promise<number>
}

export async function updateTask(id: number, updates: Partial<Task>): Promise<void> {
  // #26: 명시적 updatedAt이 전달되면 그대로 보존 (클라우드 권위 타임스탬프 유지),
  // 없으면 현재 시각으로 스탬프. 동기화 경로가 cloudTask.updatedAt을 그대로 쓰게 하여
  // 로컬이 클라우드보다 인위적으로 더 최신으로 보이는 stale write-back을 방지.
  await db.tasks.update(id, { ...updates, updatedAt: updates.updatedAt ?? new Date().toISOString() })
}

export async function deleteTask(id: number): Promise<void> {
  await db.transaction('rw', [db.tasks, db.completionLogs, db.attachments], async () => {
    await db.tasks.delete(id)
    await db.completionLogs.where('taskId').equals(id).delete()
    await db.attachments.where('taskId').equals(id).delete()
  })
}

export async function getTasksByDate(date: string): Promise<Task[]> {
  return db.tasks.where('dueDate').equals(date).toArray()
}

export async function getTasksByCategory(categoryId: number): Promise<Task[]> {
  return db.tasks.where('categoryId').equals(categoryId).toArray()
}

export async function getOverdueTasks(): Promise<Task[]> {
  const today = formatDate(new Date())
  return db.tasks
    .where('status').equals('pending')
    .filter(t => !!t.dueDate && t.dueDate < today)
    .toArray()
}

export async function getUpcomingTasks(days: number): Promise<Task[]> {
  const today = new Date()
  const end = new Date(today)
  end.setDate(end.getDate() + days)
  const todayStr = formatDate(today)
  const endStr = formatDate(end)
  return db.tasks
    .where('status').equals('pending')
    .filter(t => !!t.dueDate && t.dueDate >= todayStr && t.dueDate <= endStr)
    .toArray()
}

// ─── Bulk Task Operations ─────────────────────────
export async function bulkUpdateTasks(ids: number[], updates: Partial<Task>): Promise<void> {
  await db.transaction('rw', db.tasks, async () => {
    const now = new Date().toISOString()
    for (const id of ids) {
      await db.tasks.update(id, { ...updates, updatedAt: now })
    }
  })
}

/**
 * #37: 일괄 완료를 단일 트랜잭션으로 처리 — 작업 상태 갱신 + 완료 로그 추가를 원자적으로 수행.
 * bulkUpdateTasks/addCompletionLog를 중첩하지 않고 자체 트랜잭션 안에서 직접 처리하여
 * 부분 실패(작업은 완료됐는데 로그는 누락)를 방지한다.
 */
export async function bulkCompleteTasks(
  ids: number[],
  completedAt: string,
  logs: Omit<CompletionLog, 'id'>[],
): Promise<void> {
  await db.transaction('rw', [db.tasks, db.completionLogs], async () => {
    for (const id of ids) {
      await db.tasks.update(id, { status: 'completed', completedAt, updatedAt: completedAt })
    }
    for (const log of logs) {
      await db.completionLogs.add(log as CompletionLog)
    }
  })
}

export async function bulkDeleteTasks(ids: number[]): Promise<void> {
  await db.transaction('rw', [db.tasks, db.completionLogs, db.attachments], async () => {
    await db.tasks.bulkDelete(ids)
    for (const id of ids) {
      await db.completionLogs.where('taskId').equals(id).delete()
      await db.attachments.where('taskId').equals(id).delete()
    }
  })
}

// ─── Category CRUD ─────────────────────────────────
export async function getAllCategories(): Promise<Category[]> {
  return db.categories.orderBy('sortOrder').toArray()
}

export async function addCategory(cat: Omit<Category, 'id'>): Promise<number> {
  return db.categories.add(cat as Category) as Promise<number>
}

export async function updateCategory(id: number, updates: Partial<Category>): Promise<void> {
  await db.categories.update(id, { ...updates, updatedAt: new Date().toISOString() })
}

export async function deleteCategory(id: number): Promise<void> {
  await db.categories.delete(id)
  await db.tasks.where('categoryId').equals(id).modify({ categoryId: null })
}

// ─── CompletionLog ─────────────────────────────────
export async function addCompletionLog(log: Omit<CompletionLog, 'id'>): Promise<void> {
  await db.completionLogs.add(log as CompletionLog)
}

/** #1: syncId 기준으로 완료 로그 1건 삭제 (완료 취소/Undo 시 orphan 로그 제거용) */
export async function deleteCompletionLogBySyncId(syncId: string): Promise<void> {
  await db.completionLogs.where('syncId').equals(syncId).delete()
}

/**
 * #1: 특정 작업의 완료 로그 중 completedAt이 정확히 일치하는 1건을 찾는다.
 * 완료 취소(직접 uncomplete) 시 해당 완료에 대응하는 로그를 정밀하게 제거하기 위함.
 * 일치 항목이 없으면 가장 최근(completedAt) 로그로 폴백.
 */
export async function findCompletionLogForUncomplete(taskId: number, completedAt?: string): Promise<CompletionLog | undefined> {
  const logs = await db.completionLogs.where('taskId').equals(taskId).toArray()
  if (logs.length === 0) return undefined
  if (completedAt) {
    const exact = logs.find((l) => l.completedAt === completedAt)
    if (exact) return exact
  }
  return logs.reduce((latest, l) => (l.completedAt > latest.completedAt ? l : latest))
}

export async function getCompletionLogsByRange(start: string, end: string): Promise<CompletionLog[]> {
  return db.completionLogs
    .where('date')
    .between(start, end, true, true)
    .toArray()
}

export async function getCompletionLogsByDate(date: string): Promise<CompletionLog[]> {
  return db.completionLogs.where('date').equals(date).toArray()
}

export async function getDailyCompletionCounts(start: string, end: string): Promise<Record<string, number>> {
  const logs = await getCompletionLogsByRange(start, end)
  const counts: Record<string, number> = {}
  for (const log of logs) {
    counts[log.date] = (counts[log.date] || 0) + 1
  }
  return counts
}

export async function getTotalCompletedCount(): Promise<number> {
  return db.completionLogs.count()
}

// ─── Attachment CRUD ──────────────────────────────
export async function getAttachmentsByTask(taskId: number): Promise<Attachment[]> {
  return db.attachments.where('taskId').equals(taskId).toArray()
}

export async function addAttachment(attachment: Omit<Attachment, 'id'>): Promise<number> {
  return db.attachments.add(attachment as Attachment) as Promise<number>
}

export async function deleteAttachment(id: number): Promise<void> {
  await db.attachments.delete(id)
}

export async function getAttachmentCountByTask(taskId: number): Promise<number> {
  return db.attachments.where('taskId').equals(taskId).count()
}

// ─── Template CRUD ────────────────────────────────
export async function getAllTemplates(): Promise<TaskTemplate[]> {
  return db.templates.toArray()
}

export async function addTemplate(template: Omit<TaskTemplate, 'id'>): Promise<number> {
  return db.templates.add(template as TaskTemplate) as Promise<number>
}

export async function deleteTemplate(id: number): Promise<void> {
  await db.templates.delete(id)
}

// ─── Activity Log CRUD ────────────────────────────
export async function getActivityLogs(limit: number = 100): Promise<ActivityLog[]> {
  return db.activityLogs.orderBy('createdAt').reverse().limit(limit).toArray()
}

export async function getActivityLogsByDateRange(start: string, end: string): Promise<ActivityLog[]> {
  return db.activityLogs
    .where('date')
    .between(start, end, true, true)
    .reverse()
    .toArray()
}

let _activityLogAddCount = 0

export async function addActivityLog(log: Omit<ActivityLog, 'id'>): Promise<void> {
  await db.activityLogs.add(log as ActivityLog)
  // 자동 정리: 100번째 추가마다 1000개 초과 시 오래된 항목 삭제
  _activityLogAddCount++
  if (_activityLogAddCount % 100 === 0) {
    const count = await db.activityLogs.count()
    if (count > 1000) {
      const oldest = await db.activityLogs.orderBy('createdAt').limit(count - 1000).toArray()
      await db.activityLogs.bulkDelete(oldest.map(l => l.id!))
    }
  }
}

export async function clearOldActivityLogs(daysToKeep: number): Promise<void> {
  const { format } = await import('date-fns')
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - daysToKeep)
  const cutoffStr = format(cutoff, 'yyyy-MM-dd')
  await db.activityLogs.where('date').below(cutoffStr).delete()
}

// ─── Sync Helpers ─────────────────────────────────
export function generateSyncId(): string {
  return crypto.randomUUID()
}

export async function getTaskBySyncId(syncId: string): Promise<Task | undefined> {
  return db.tasks.where('syncId').equals(syncId).first()
}

export async function getCategoryBySyncId(syncId: string): Promise<Category | undefined> {
  return db.categories.where('syncId').equals(syncId).first()
}

export async function getCompletionLogBySyncId(syncId: string): Promise<CompletionLog | undefined> {
  return db.completionLogs.where('syncId').equals(syncId).first()
}

// ─── Task Group CRUD ─────────────────────────────
export async function getAllTaskGroups(): Promise<TaskGroup[]> {
  return db.taskGroups.orderBy('sortOrder').toArray()
}

export async function addTaskGroup(group: Omit<TaskGroup, 'id'>): Promise<number> {
  return db.taskGroups.add(group as TaskGroup) as Promise<number>
}

export async function updateTaskGroup(id: number, updates: Partial<TaskGroup>): Promise<void> {
  await db.taskGroups.update(id, { ...updates, updatedAt: new Date().toISOString() })
}

export async function deleteTaskGroup(id: number): Promise<void> {
  await db.taskGroups.delete(id)
}

export async function getTaskGroupBySyncId(syncId: string): Promise<TaskGroup | undefined> {
  return db.taskGroups.where('syncId').equals(syncId).first()
}

// ─── Bulk Operations ───────────────────────────────
export async function clearAllData(): Promise<void> {
  await db.tasks.clear()
  await db.categories.clear()
  await db.completionLogs.clear()
  await db.attachments.clear()
  await db.templates.clear()
  await db.activityLogs.clear()
  await db.taskGroups.clear()
  await db.categories.bulkAdd([
    { name: '작업', color: '#3B82F6', isDefault: true, sortOrder: 0, createdAt: new Date().toISOString() },
    { name: '개인', color: '#10B981', isDefault: true, sortOrder: 1, createdAt: new Date().toISOString() },
    { name: '위시리스트', color: '#F59E0B', isDefault: true, sortOrder: 2, createdAt: new Date().toISOString() },
  ])
}
